import { TreeNode } from '../../../types/interfaces';
import { EntityVisibility } from '../EntityVisibility';
import { CalendarViewMode, SortConfig } from '../types';

import { useLocalStorageState } from './useLocalStorageState';

const DEFAULT_VISIBLE_ENTITIES: EntityVisibility = {
  all: true,
  plannedShifts: true,
  manualShifts: true,
  departmentSchedule: true,
  absences: true,
  closedTask: true,
  notClosedTask: true
};

const DEFAULT_SORT_CONFIG: SortConfig = { sortBy: 'none', sortDirection: 'none' };

export function useCalendarAssignmentsPersistedState() {
  const [viewMode, setViewMode] = useLocalStorageState<CalendarViewMode>(
    'calendarViewMode',
    () => 'schedule',
    {
      serialize: (v) => v,
      deserialize: (raw) =>
        raw === 'schedule' || raw === 'timesheet' ? raw : 'schedule'
    }
  );

  const [visibleEntities, setVisibleEntities] = useLocalStorageState<EntityVisibility>(
    'calendarVisibleEntities',
    () => DEFAULT_VISIBLE_ENTITIES,
    {
      serialize: (v) => JSON.stringify(v),
      deserialize: (raw) => {
        try {
          return JSON.parse(raw) as EntityVisibility;
        } catch {
          return DEFAULT_VISIBLE_ENTITIES;
        }
      }
    }
  );

  const [showOnlyFreeEmployees, setShowOnlyFreeEmployees] = useLocalStorageState<boolean>(
    'calendarShowOnlyFreeEmployees',
    () => false,
    { serialize: (v) => v.toString(), deserialize: (raw) => raw === 'true' }
  );

  const [excludeLongAbsences, setExcludeLongAbsences] = useLocalStorageState<boolean>(
    'calendarExcludeLongAbsences',
    () => false,
    { serialize: (v) => v.toString(), deserialize: (raw) => raw === 'true' }
  );

  const [todayShiftOnly, setTodayShiftOnly] = useLocalStorageState<boolean>(
    'calendarTodayShiftOnly',
    () => false,
    { serialize: (v) => v.toString(), deserialize: (raw) => raw === 'true' }
  );

  const [sortConfig, setSortConfig] = useLocalStorageState<SortConfig>(
    'sortConfigCalendarAssignments',
    () => DEFAULT_SORT_CONFIG,
    {
      serialize: (v) => JSON.stringify(v),
      deserialize: (raw) => {
        try {
          return JSON.parse(raw) as SortConfig;
        } catch {
          return DEFAULT_SORT_CONFIG;
        }
      }
    }
  );

  const [storedDepartmentFilterRaw, setStoredDepartmentFilterRaw] =
    useLocalStorageState<string | null>(
      'storedDepartmentFilter',
      () => null,
      {
        serialize: (v) => v ?? '',
        deserialize: (raw) => raw
      }
    );

  const persistDepartmentFilter = (selection: TreeNode | null) => {
    setStoredDepartmentFilterRaw(JSON.stringify(selection));
  };

  return {
    viewMode,
    setViewMode,
    visibleEntities,
    setVisibleEntities,
    showOnlyFreeEmployees,
    setShowOnlyFreeEmployees,
    excludeLongAbsences,
    setExcludeLongAbsences,
    todayShiftOnly,
    setTodayShiftOnly,
    sortConfig,
    setSortConfig,
    storedDepartmentFilterRaw,
    persistDepartmentFilter
  };
}

